import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Globe,
  Loader2,
  Sparkles,
  Wifi,
  WifiOff,
  Square,
  History,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  createScrapeProductsService,
  type ScrapeJobProgress,
  type ScrapeProductsService,
} from '@/services/scrapeProducts';
import { toast } from 'sonner';
import { ScrapeHistoryModal } from './ScrapeHistoryModal';

interface ScrapeProductsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScrapeProductsModal({
  open,
  onOpenChange,
}: ScrapeProductsModalProps) {
  const [url, setUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [jobProgress, setJobProgress] = useState<ScrapeJobProgress | null>(
    null
  );
  const [isStopping, setIsStopping] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const scrapeServiceRef = useRef<ScrapeProductsService | null>(null);

  const handleConnectionStatusChange = useCallback((connected: boolean) => {
    setIsConnected(connected);
    setIsConnecting(false);
  }, []);

  const handleJobProgress = useCallback((progress: ScrapeJobProgress) => {
    setJobProgress(progress);

    if (progress.status === 'completed') {
      setIsScraping(false);
      setIsStopping(false);
      toast.success('Scraping Completed', {
        description: `Successfully scraped ${progress.completedCount} products`,
      });
    } else if (progress.status === 'failed') {
      setIsScraping(false);
      setIsStopping(false);
      toast.error('Scraping Failed', {
        description: 'Failed to scrape products from the website',
      });
    } else if (progress.isStopped) {
      setIsScraping(false);
      setIsStopping(false);
      toast.info('Scraping Stopped', {
        description: 'The scraping process has been stopped',
      });
    }
  }, []);

  const handleScrapeError = useCallback((error: { message: string }) => {
    setError(error.message);
    setIsScraping(false);
    setIsConnecting(false);
    toast.error('Scraping Error', {
      description: error.message,
    });
  }, []);

  // Initialize socket connection when modal opens
  useEffect(() => {
    if (open) {
      scrapeServiceRef.current = createScrapeProductsService();
      setIsConnecting(true);

      scrapeServiceRef.current.connectSocket({
        onConnectionStatusChange: handleConnectionStatusChange,
        onJobProgress: handleJobProgress,
        onScrapeError: handleScrapeError,
      });
    }

    return () => {
      if (scrapeServiceRef.current) {
        scrapeServiceRef.current.disconnectSocket();
        scrapeServiceRef.current = null;
      }
    };
  }, [
    open,
    handleConnectionStatusChange,
    handleJobProgress,
    handleScrapeError,
  ]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    setError(null);
  };

  const validateUrl = (urlString: string): boolean => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleScrape = async () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    const trimmedUrl = url.trim();
    if (!validateUrl(trimmedUrl)) {
      setError(
        'Please enter a valid URL (must start with http:// or https://)'
      );
      return;
    }

    if (!isConnected) {
      setError('Socket not connected. Please wait...');
      setIsConnecting(true);
      scrapeServiceRef.current?.connectSocket({
        onConnectionStatusChange: handleConnectionStatusChange,
        onJobProgress: handleJobProgress,
        onScrapeError: handleScrapeError,
      });
      return;
    }

    setIsScraping(true);
    setError(null);
    setJobProgress(null);

    try {
      await scrapeServiceRef.current?.scrapeProducts(trimmedUrl);
      toast.success('Scraping Started', {
        description: 'The scraping process has been initiated',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scraping');
      setIsScraping(false);
    }
  };

  const handleStop = async () => {
    if (!jobProgress?.id) {
      return;
    }

    if (!scrapeServiceRef.current?.isConnected()) {
      toast.error('Connection Error', {
        description: 'Socket is not connected. Please wait...',
      });
      return;
    }

    setIsStopping(true);
    try {
      await scrapeServiceRef.current?.stopScrapeJob(jobProgress.id);
      // Socket event yuborildi, success kelganda toast ko'rsatmaymiz
      // isStopped: true kelguncha loading holatida qoladi
      // handleJobProgress funksiyasida isStopped: true kelganda loading to'xtatiladi
    } catch (err) {
      setIsStopping(false);
      toast.error('Failed to Stop', {
        description:
          err instanceof Error ? err.message : 'Could not stop scraping',
      });
    }
    // finally blokini olib tashladik, chunki isStopped: true kelguncha loading qoladi
  };

  const getProgressPercentage = (): number => {
    if (!jobProgress || jobProgress.totalCount === 0) return 0;
    return Math.round(
      (jobProgress.completedCount / jobProgress.totalCount) * 100
    );
  };

  const canClose =
    !isScraping &&
    (!jobProgress ||
      jobProgress.isStopped ||
      jobProgress.status === 'completed' ||
      jobProgress.status === 'failed');

  const handleClose = () => {
    if (canClose) {
      setUrl('');
      setError(null);
      setJobProgress(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={canClose ? handleClose : undefined}>
      <DialogContent className="!max-w-[600px] w-full pt-">
        <DialogHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="h-4 w-4 text-primary" />
                Get Products from Website
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-800 border-green-200"
                >
                  <Wifi className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge
                  variant="destructive"
                  className="bg-red-100 text-red-800 border-red-200"
                >
                  <WifiOff className="h-3 w-3 mr-1" />
                  {isConnecting ? 'Connecting...' : 'Disconnected'}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <DialogDescription className="text-sm">
              Enter the website URL to scrape products.
            </DialogDescription>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsHistoryOpen(true)}
              className="h-8"
            >
              <History className="h-4 w-4 mr-1.5" />
              History
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="product-url" className="text-sm font-medium">
              Product URL
            </Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="product-url"
                type="url"
                placeholder="https://www.example.com/products"
                value={url}
                onChange={handleUrlChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isScraping && isConnected) {
                    handleScrape();
                  }
                }}
                disabled={isScraping || !isConnected}
                className="pl-10"
              />
            </div>
            {error && (
              <Alert variant="destructive" className="mt-1.5 py-2">
                <AlertDescription className="text-xs">{error}</AlertDescription>
              </Alert>
            )}
          </div>

          {jobProgress && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Scraping Progress</span>
                  {jobProgress.status === 'completed' ? (
                    <Badge
                      variant="default"
                      className="bg-green-100 text-green-800"
                    >
                      Completed
                    </Badge>
                  ) : jobProgress.status === 'failed' ? (
                    <Badge variant="destructive">Failed</Badge>
                  ) : jobProgress.isStopped ? (
                    <Badge variant="secondary">Stopped</Badge>
                  ) : jobProgress.status === 'in_progress' ? (
                    <Badge
                      variant="default"
                      className="bg-blue-100 text-blue-800"
                    >
                      In Progress
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span>
                    {jobProgress.completedCount} / {jobProgress.totalCount}
                  </span>
                  <span className="font-medium">
                    {getProgressPercentage()}%
                  </span>
                </div>
              </div>
              <Progress value={getProgressPercentage()} className="h-2" />
              {(jobProgress.status === 'in_progress' &&
                !jobProgress.isStopped) ||
              isStopping ? (
                <div className="space-y-2">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleStop}
                      disabled={isStopping}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {isStopping ? (
                        <>
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          Stopping...
                        </>
                      ) : (
                        <>
                          <Square className="mr-2 h-3.5 w-3.5" />
                          Stop Scraping
                        </>
                      )}
                    </Button>
                  </div>
                  {isStopping && (
                    <div className="flex justify-end">
                      <p className="text-xs text-muted-foreground">
                        ⚠️ This may take a few moments. Please wait and do not
                        close this window.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={!canClose}
            >
              {jobProgress ? 'Close' : 'Cancel'}
            </Button>
            <Button
              size="sm"
              onClick={handleScrape}
              disabled={
                isScraping ||
                !url.trim() ||
                !isConnected ||
                (jobProgress?.status === 'in_progress' &&
                  !jobProgress.isStopped)
              }
              className="min-w-[130px]"
            >
              {isScraping ||
              (jobProgress?.status === 'in_progress' &&
                !jobProgress.isStopped) ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Scraping...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-3.5 w-3.5" />
                  Scrape Products
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
      <ScrapeHistoryModal
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
      />
    </Dialog>
  );
}
